import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuList,
} from "../../components/ui/navigation-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../../components/ui/sheet";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { Menu } from "lucide-react";
import { ProfileToggle } from "./profile-toggle";
import { selectIsAuthenticated, logout } from "../../features/auth/authSlice";
import logger from "../../utils/logger";
import { Logo } from "./Logo";

const providerAppUrl =
  process.env.REACT_APP_PROVIDER_APP_URL || "http://pro.localhost:3000";

const routeList = [
  {
    to: "/services",
    label: "All Services",
  },
  {
    to: "/bookings",
    label: "My Bookings",
  },
];

const homeSections = [
  {
    to: "#how-it-works",
    label: "How it works",
  },
  {
    to: "/services",
    label: "All Services",
  },
  {
    to: `${providerAppUrl}/marketing`,
    label: "Become a Provider",
    external: true,
  },
];

export const Navbar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);

  const isAuthenticated = useSelector(selectIsAuthenticated);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 0) {
        setHasScrolled(true);
      } else {
        setHasScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Handle anchor link navigation with smooth scroll
  const handleAnchorClick = (e, to) => {
    if (to.startsWith("#")) {
      e.preventDefault();
      const elementId = to.slice(1);

      // If we're not on the homepage, navigate to homepage and scroll
      if (location.pathname !== "/") {
        setIsOpen(false);
        navigate("/");
        // Wait for navigation to complete, then scroll
        setTimeout(() => {
          const element = document.getElementById(elementId);
          if (element) {
            element.scrollIntoView({ behavior: "smooth" });
          }
        }, 100);
        return;
      }

      // If we're on homepage, smooth scroll to section
      const element = document.getElementById(elementId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
        setIsOpen(false);
      }
    } else {
      setIsOpen(false);
    }
  };

  const handleLogout = async () => {
    try {
      await dispatch(logout()).unwrap();
      logger.info("User logged out successfully");
      navigate("/");
      setIsOpen(false); // Close mobile menu if open
    } catch (error) {
      logger.error("Logout failed:", error);
      // Even if API fails, we cleared local state in the thunk
      navigate("/");
      setIsOpen(false);
    }
  };

  return (
    <header
      className={`sticky top-0 z-40 w-full mt-[-56px] ${
        hasScrolled
          ? "bg-white/70 backdrop-blur-md dark:bg-background/70 dark:border-b-slate-700"
          : "bg-transparent dark:border-b-slate-700"
      }`}
    >
      <NavigationMenu className="mx-auto">
        <NavigationMenuList className="container h-14 px-4 w-screen flex justify-between ">
          <NavigationMenuItem className="font-bold flex">
            <Link
              to="/"
              className="ml-2 font-semibold text-xl flex font-serif2 items-center"
            >
              <Logo />
            </Link>
          </NavigationMenuItem>

          {/* mobile */}
          <span className="flex md:hidden gap-4 items-center">
            {isAuthenticated ? (
              <ProfileToggle onLogout={handleLogout} />
            ) : (
              <>
                <Link
                  to="/auth/login"
                  className="text-[16px] text-foreground hover:text-foreground/80 transition-colors"
                >
                  Log In
                </Link>
                <Link to="/auth/signup">
                  <Button className="text-[16px]">Sign Up</Button>
                </Link>
              </>
            )}
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger className="px-2" aria-label="Open menu">
                <Menu className="flex md:hidden h-5 w-5" />
              </SheetTrigger>

              <SheetContent side={"left"}>
                <SheetHeader>
                  <SheetTitle className="font-bold text-xl flex text-center">
                    <Logo className="inline-block h-9 w-auto" />
                  </SheetTitle>
                </SheetHeader>
                <nav className="flex flex-col justify-center items-center gap-2 mt-4">
                  {(isAuthenticated ? routeList : homeSections).map(
                    ({ to, label, external }) =>
                      external ? (
                        <a
                          key={label}
                          href={to}
                          onClick={() => setIsOpen(false)}
                          className="text-muted-foreground transition-colors hover:text-foreground"
                        >
                          {label}
                        </a>
                      ) : to.startsWith("#") ? (
                        <a
                          key={label}
                          href={to}
                          onClick={(e) => handleAnchorClick(e, to)}
                          className="text-muted-foreground transition-colors hover:text-foreground"
                        >
                          {label}
                        </a>
                      ) : (
                        <Link
                          key={label}
                          to={to}
                          onClick={() => setIsOpen(false)}
                          className="text-muted-foreground transition-colors hover:text-foreground"
                        >
                          {label}
                        </Link>
                      ),
                  )}
                </nav>
              </SheetContent>
            </Sheet>
          </span>

          {/* desktop */}
          <div className="hidden flex gap-8 md:flex justify-center items-center">
            {isAuthenticated ? (
              <nav className="hidden md:flex gap-6">
                {routeList.map(({ to, label }) => (
                  <Link
                    key={label}
                    to={to}
                    onClick={() => setIsOpen(false)}
                    className="text-[16px] text-muted-foreground transition-colors hover:text-foreground"
                  >
                    {label}
                  </Link>
                ))}
              </nav>
            ) : (
              <nav className="hidden md:flex gap-6">
                {homeSections.map(({ to, label, external }) =>
                  external ? (
                    <a
                      key={label}
                      href={to}
                      className="text-[16px] text-muted-foreground transition-colors hover:text-foreground"
                    >
                      {label}
                    </a>
                  ) : (
                    <a
                      key={label}
                      href={to}
                      onClick={(e) => handleAnchorClick(e, to)}
                      className="text-[16px] text-muted-foreground transition-colors hover:text-foreground"
                    >
                      {label}
                    </a>
                  ),
                )}
              </nav>
            )}

            <div
              className={`hidden md:flex gap-4 ${
                !isAuthenticated ? "pl-12 border-l border-slate-200" : ""
              } items-center`}
            >
              {isAuthenticated ? (
                <ProfileToggle onLogout={handleLogout} />
              ) : (
                <>
                  <Link
                    to="/auth/login"
                    className="text-[16px] text-foreground hover:text-foreground/80 transition-colors"
                  >
                    Log In
                  </Link>
                  <Link to="/auth/signup">
                    <Button className="text-[16px]">Sign Up</Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </NavigationMenuList>
      </NavigationMenu>
    </header>
  );
};
